import { createContext, useContext, useMemo, useCallback } from "react";
import { toast } from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../services/notification";
import { getUserIdentity } from "../utils/authorization";
// Get user ID from localStorage
const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.id || null;
  } catch (error) {
    console.error("Error getting user ID:", error);
    return null;
  }
};

const NotificationContext = createContext(undefined);

export const NotificationProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const userId = getUserId();
  const isNurse = getUserIdentity()?.isNurse ?? false;

  const {
    data: notificationsData,
    isLoading: loading,
    error,
  } = useQuery(["notifications", userId], () => getNotifications(userId), {
    enabled: !!userId && isNurse,
    onError: (err) => {
      console.error("Error fetching notifications:", err);
      toast.error("Failed to load notifications");
    },
    refetchInterval: 60000,
  });

  const notifications = notificationsData?.data || [];

  const markAsReadMutation = useMutation(
    (notificationId) => markNotificationAsRead(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notifications", userId]);
      },
      onError: (err) => {
        console.error("Error marking notification as read:", err);
        toast.error("Failed to mark notification as read");
      },
    }
  );

  const markAllAsReadMutation = useMutation(
    () => markAllNotificationsAsRead(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notifications", userId]);
        toast.success("All notifications marked as read");
      },
      onError: (err) => {
        console.error("Error marking all as read:", err);
        toast.error("Failed to mark all as read");
      },
    }
  );

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.is_read).length;
  }, [notifications]);

  const readCount = useMemo(() => {
    return notifications.filter((n) => n.is_read).length;
  }, [notifications]);

  const totalCount = useMemo(() => {
    return notifications.length;
  }, [notifications]);

  const handleMarkAsRead = useCallback(
    (notificationId) => {
      markAsReadMutation.mutate(notificationId);
    },
    [markAsReadMutation]
  );

  const handleMarkAllAsRead = useCallback(() => {
    if (!userId) {
      toast.error("User ID not found");
      return;
    }
    markAllAsReadMutation.mutate();
  }, [userId, markAllAsReadMutation]);

  const refetchNotifications = useCallback(() => {
    queryClient.invalidateQueries(["notifications", userId]);
  }, [queryClient, userId]);

  const value = useMemo(
    () => ({
      notifications,
      loading,
      error,
      unreadCount,
      readCount,
      totalCount,
      markAsRead: handleMarkAsRead,
      markAllAsRead: handleMarkAllAsRead,
      refetchNotifications,
      isMarkingAsRead: markAsReadMutation.isLoading,
      isMarkingAllAsRead: markAllAsReadMutation.isLoading,
    }),
    [
      notifications,
      loading,
      error,
      unreadCount,
      readCount,
      totalCount,
      handleMarkAsRead,
      handleMarkAllAsRead,
      refetchNotifications,
      markAsReadMutation.isLoading,
      markAllAsReadMutation.isLoading,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
